import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import Button from "@material-tailwind/react/Button";
import Modal from "@material-tailwind/react/Modal";
import ModalHeader from "@material-tailwind/react/ModalHeader";
import ModalBody from "@material-tailwind/react/ModalBody";
import ModalFooter from "@material-tailwind/react/ModalFooter";
import Card from "@material-tailwind/react/Card";
import CardBody from "@material-tailwind/react/CardBody";
import Input from "@material-tailwind/react/Input";
import { toast } from "react-toastify";
import { fetchPost } from "../../../../fetchers";
import { useMutation } from "@tanstack/react-query";
import Loading from "../../../../components/loading/Loading";
import { GetKaryawanMinV2, karyawanSelector } from "../../karyawan/karyawanSlice";
import { Colors } from "../../../../assets/Colors";
import Select from "react-select";

const InputSaldoCutiPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const karyawan = useSelector(karyawanSelector);
  const karyawanData = karyawan.minKaryawan || karyawan.minData || [];

  const [showModal, setShowModal] = useState(false);
  const [modalType, setModalType] = useState("add");
  const [selectedData, setSelectedData] = useState(null);

  const [formData, setFormData] = useState({
    nik: "",
    nama_karyawan: "",
    jumlah: "",
    tahun: new Date().getFullYear(),
    keterangan: "",
    tkontrak_kerja_id: "", // "" = per tahun; terisi = hak cuti satu periode kontrak PKWT
  });
  // Kontrak PKWT aktif karyawan terpilih — saldo PKWT berlaku per periode kontrak.
  const [kontrakList, setKontrakList] = useState([]);

  const [filterTahun, setFilterTahun] = useState(new Date().getFullYear());
  const [search, setSearch] = useState("");
  const [saldoCutiList, setSaldoCutiList] = useState([]);
  const [loading, setLoading] = useState(false);

  // Fetch karyawan list for dropdown
  useEffect(() => {
    dispatch(GetKaryawanMinV2({ search: "" }));
  }, []);

  // Fetch saldo cuti list on mount and when filter changes
  useEffect(() => {
    if (filterTahun) {
      loadSaldoCutiList(filterTahun, search);
    }
  }, [filterTahun, search]);

  const loadSaldoCutiList = async (tahun, searchParam) => {
    setLoading(true);
    try {
      const response = await fetchPost("hrd/GetSaldoCutiListByYear", {
        tahun: tahun,
        search: searchParam,
      });

      if (response.status === "Success") {
        setSaldoCutiList(response.data || []);
      } else {
        setSaldoCutiList([]);
        toast.error(response.message || "Gagal memuat data saldo cuti");
      }
    } catch (error) {
      console.error("Error loading saldo cuti:", error);
      toast.error("Terjadi kesalahan saat memuat data");
    } finally {
      setLoading(false);
    }
  };

  // Handle input change
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const loadKontrak = async (nik) => {
    setKontrakList([]);
    if (!nik) return [];
    try {
      const response = await fetchPost("hrd/GetKontrakPKWTByNik", { nik });
      const list = response.status === "Success" ? response.data || [] : [];
      setKontrakList(list);
      return list;
    } catch (error) {
      return [];
    }
  };

  // Handle karyawan selection
  const handleKaryawanSelect = async (nik) => {
    const selectedKaryawan = karyawanData.find((k) => k.value === nik);
    if (selectedKaryawan) {
      setFormData((prev) => ({
        ...prev,
        nik: nik,
        nama_karyawan: selectedKaryawan.label,
        tkontrak_kerja_id: "",
      }));
      const list = await loadKontrak(nik);
      // PKWT ber-kontrak: default ke kontrak terbaru (saldo PKWT per periode kontrak).
      if (list.length > 0) {
        setFormData((prev) => ({ ...prev, tkontrak_kerja_id: list[0].tkontrak_kerja_id }));
      }
    }
  };

  // Mutations
  const insertMutation = useMutation((data) =>
    fetchPost("hrd/InsertSaldoCutiManual", data)
  );

  const updateMutation = useMutation((data) =>
    fetchPost("hrd/UpdateSaldoCuti", data)
  );

  const deleteMutation = useMutation((data) =>
    fetchPost("hrd/DeleteSaldoCuti", data)
  );

  // Handle submit
  const handleSubmit = async () => {
    // Validation
    if (!formData.nik) {
      toast.error("NIK wajib dipilih");
      return;
    }
    if (!formData.jumlah || formData.jumlah < 1) {
      toast.error("Jumlah cuti minimal 1");
      return;
    }
    if (!formData.tahun && !formData.tkontrak_kerja_id) {
      toast.error("Tahun wajib diisi");
      return;
    }

    const payload = {
      nik: formData.nik,
      jumlah: parseInt(formData.jumlah),
      tahun: parseInt(formData.tahun) || new Date().getFullYear(),
      keterangan: formData.keterangan || "",
      tkontrak_kerja_id: formData.tkontrak_kerja_id || "",
    };

    try {
      setLoading(true);
      const response = await insertMutation.mutateAsync(payload);

      if (response.status === "Success") {
        toast.success("Berhasil menambahkan saldo cuti");
        setShowModal(false);
        resetForm();
        loadSaldoCutiList(filterTahun, search);
      } else {
        toast.error(response.message || "Gagal menambahkan saldo cuti");
      }
    } catch (error) {
      console.error("Error inserting saldo cuti:", error);
      toast.error(error.response?.data?.message || "Terjadi kesalahan");
    } finally {
      setLoading(false);
    }
  };

  // Handle update
  const handleUpdate = async () => {
    if (!formData.jumlah || formData.jumlah < 0) {
      toast.error("Jumlah cuti tidak boleh negatif");
      return;
    }

    const payload = {
      saldo_cuti_id: selectedData.saldo_cuti_id,
      jumlah: parseInt(formData.jumlah),
      keterangan: formData.keterangan || "",
    };

    try {
      setLoading(true);
      const response = await updateMutation.mutateAsync(payload);

      if (response.status === "Success") {
        toast.success("Berhasil mengubah saldo cuti");
        setShowModal(false);
        resetForm();
        loadSaldoCutiList(filterTahun, search);
      } else {
        toast.error(response.message || "Gagal mengubah saldo cuti");
      }
    } catch (error) {
      console.error("Error updating saldo cuti:", error);
      toast.error(error.response?.data?.message || "Terjadi kesalahan");
    } finally {
      setLoading(false);
    }
  };

  // Handle delete
  const handleDelete = async () => {
    const payload = {
      saldo_cuti_id: parseInt(selectedData.saldo_cuti_id),
    };
    
    console.log("Deleting saldo cuti with ID:", payload.saldo_cuti_id);

    try {
      setLoading(true);
      const response = await deleteMutation.mutateAsync(payload);

      if (response.status === "Success") {
        toast.success("Berhasil menghapus saldo cuti");
        setShowModal(false);
        setSelectedData(null);
        loadSaldoCutiList(filterTahun, search);
      } else {
        toast.error(response.message || "Gagal menghapus saldo cuti");
      }
    } catch (error) {
      console.error("Error deleting saldo cuti:", error);
      toast.error(error.response?.data?.message || "Terjadi kesalahan");
    } finally {
      setLoading(false);
    }
  };

  // Reset form
  const resetForm = () => {
    setFormData({
      nik: "",
      nama_karyawan: "",
      jumlah: "",
      tahun: new Date().getFullYear(),
      keterangan: "",
      tkontrak_kerja_id: "",
    });
    setKontrakList([]);
    setSelectedData(null);
  };

  // Open modal for add
  const openAddModal = () => {
    resetForm();
    setModalType("add");
    setShowModal(true);
  };

  // Open modal for edit
  const openEditModal = (data) => {
    console.log("Open edit modal with data:", data);
    setFormData({
      nik: data.nik,
      nama_karyawan: data.nama_karyawan,
      jumlah: data.jumlah.toString(),
      tahun: data.tahun,
      keterangan: data.keterangan || "",
      tkontrak_kerja_id: data.tkontrak_kerja_id || "",
    });
    setSelectedData(data);
    setModalType("edit");
    setShowModal(true);
  };

  // Open modal for delete confirmation
  const openDeleteModal = (data) => {
    console.log("Open delete modal with data:", data);
    console.log("SaldoCutiID to delete:", data.saldo_cuti_id);
    setSelectedData(data);
    setModalType("delete");
    setShowModal(true);
  };

  // Handle search - auto search saat typing
  const handleSearch = (e) => {
    setSearch(e.target.value);
  };

  // Handle auto generate
  const handleAutoGenerate = async () => {
    if (!window.confirm("Generate saldo cuti untuk tahun depan?")) {
      return;
    }

    try {
      setLoading(true);
      const response = await fetchPost("hrd/AutoGenerateSaldoCutiTahunan", {});

      if (response.status === "Success") {
        toast.success(
          `Berhasil generate ${response.data.total_inserted} saldo cuti`
        );
        loadSaldoCutiList(filterTahun, search);
      } else {
        toast.error(response.message || "Gagal auto generate");
      }
    } catch (error) {
      console.error("Error auto generate:", error);
      toast.error("Terjadi kesalahan saat auto generate");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {loading && <Loading />}

      <div className="md:ml-64">
        <div
          style={{ background: Colors.primary }}
          className="pt-14 pb-16 px-3 md:px-8 h-auto"
        >
          <div className="container mx-auto max-w-full">
            <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-4"></div>
          </div>
        </div>

        <div className="px-3 md:px-8 h-auto -mt-32">
          <div className="container mx-auto max-w-full">
            <div className="grid grid-cols-1 px-4">
              {/* Header Buttons */}
              <div className="flex flex-wrap items-center justify-between mb-4">
                <h3 className="text-2xl font-bold text-white">
                  Input Saldo Cuti Karyawan
                </h3>
                <div className="flex flex-wrap gap-2">
                  <Button
                    color="white"
                    buttonType="outline"
                    size="md"
                    onClick={() => navigate("/human-resource/transaksi/cuti")}
                  >
                    <i className="material-icons mr-2">arrow_back</i>
                    Kembali
                  </Button>
                  <Button
                    color="white"
                    buttonType="solid"
                    size="md"
                    onClick={openAddModal}
                  >
                    <i className="material-icons mr-2">add</i>
                    Tambah Saldo Cuti
                  </Button>
                </div>
              </div>

              {/* Filters + Data Table Card */}
              <Card>
                <CardBody>
                  {/* Filter Section */}
                  <div className="flex flex-wrap items-center justify-between mb-6 pb-4 border-b border-gray-200">
                    <h4 className="text-lg font-bold text-gray-700 mb-4 md:mb-0">
                      Filter Data
                    </h4>
                    <div className="flex flex-wrap gap-4 items-end w-full md:w-auto">
                      <div className="flex-1 min-w-48">
                        <label className="block text-sm font-bold text-gray-700 mb-2">
                          Filter Tahun
                        </label>
                        <Input
                          type="number"
                          value={filterTahun}
                          onChange={(e) => setFilterTahun(parseInt(e.target.value))}
                          color="blue"
                        />
                      </div>
                      <div className="flex-1 min-w-64">
                        <label className="block text-sm font-bold text-gray-700 mb-2">
                          Cari NIK/Nama
                        </label>
                        <Input
                          type="text"
                          value={search}
                          onChange={handleSearch}
                          placeholder="Ketik NIK atau nama..."
                          color="blue"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Data Table Section */}
                  {saldoCutiList.length === 0 ? (
                    <div className="text-center py-12 text-gray-500">
                      <i className="material-icons text-6xl mb-4">inbox</i>
                      <p className="text-lg">Belum ada data saldo cuti</p>
                      <p className="text-sm mt-2">
                        Klik "Tambah Saldo Cuti" untuk menambah data
                      </p>
                    </div>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full border-collapse">
                        <thead>
                          <tr>
                            <th className="px-4 py-3 text-left text-sm font-bold text-gray-700 bg-gray-100 border-b">
                              No
                            </th>
                            <th className="px-4 py-3 text-left text-sm font-bold text-gray-700 bg-gray-100 border-b">
                              NIK
                            </th>
                            <th className="px-4 py-3 text-left text-sm font-bold text-gray-700 bg-gray-100 border-b">
                              Nama Karyawan
                            </th>
                            <th className="px-4 py-3 text-left text-sm font-bold text-gray-700 bg-gray-100 border-b">
                              Periode
                            </th>
                            <th className="px-4 py-3 text-left text-sm font-bold text-gray-700 bg-gray-100 border-b">
                              Jumlah Cuti
                            </th>
                            <th className="px-4 py-3 text-left text-sm font-bold text-gray-700 bg-gray-100 border-b">
                              Keterangan
                            </th>
                            <th className="px-4 py-3 text-center text-sm font-bold text-gray-700 bg-gray-100 border-b">
                              Aksi
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {saldoCutiList.map((item, index) => (
                            <tr key={item.saldo_cuti_id} className="hover:bg-gray-50">
                              <td className="px-4 py-3 text-sm text-gray-700 border-b">
                                {index + 1}
                              </td>
                              <td className="px-4 py-3 text-sm text-gray-700 border-b">
                                {item.nik}
                              </td>
                              <td className="px-4 py-3 text-sm text-gray-700 border-b">
                                {item.nama_karyawan}
                              </td>
                              <td className="px-4 py-3 text-sm text-gray-700 border-b">
                                {item.periode || `Tahun ${item.tahun}`}
                              </td>
                              <td className="px-4 py-3 text-sm text-gray-700 border-b">
                                <span className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-xs font-semibold">
                                  {item.jumlah} hari
                                </span>
                              </td>
                              <td className="px-4 py-3 text-sm text-gray-700 border-b">
                                {item.keterangan || "-"}
                              </td>
                              <td className="px-4 py-3 text-sm text-center border-b">
                                <Button
                                  color="green"
                                  buttonType="outline"
                                  size="sm"
                                  className="mr-2"
                                  onClick={() => openEditModal(item)}
                                >
                                  <i className="material-icons text-sm">edit</i>
                                </Button>
                                <Button
                                  color="red"
                                  buttonType="outline"
                                  size="sm"
                                  onClick={() => openDeleteModal(item)}
                                >
                                  <i className="material-icons text-sm">delete</i>
                                </Button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </CardBody>
              </Card>
            </div>
          </div>
        </div>
      </div>

      {/* Modal Add/Edit/Delete */}
      <Modal
        size="md"
        active={showModal}
        toggler={() => {
          setShowModal(false);
          resetForm();
        }}
      >
        <ModalHeader
          toggler={() => {
            setShowModal(false);
            resetForm();
          }}
        >
          {modalType === "add" && "Tambah Saldo Cuti"}
          {modalType === "edit" && "Edit Saldo Cuti"}
          {modalType === "delete" && "Hapus Saldo Cuti"}
        </ModalHeader>
        <ModalBody>
          {modalType === "delete" ? (
            <div className="text-center">
              <i className="material-icons text-6xl text-red-500 mb-4">
                warning
              </i>
              <p className="text-lg text-gray-700">
                Apakah Anda yakin ingin menghapus saldo cuti ini?
              </p>
              <p className="text-sm text-gray-500 mt-2">
                NIK: {selectedData?.nik} | {selectedData?.periode || `Tahun ${selectedData?.tahun}`}
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">
                  Pilih Karyawan *
                </label>
                <Select
                  value={karyawanData.find((k) => k.value === formData.nik) || null}
                  onChange={(selected) => handleKaryawanSelect(selected?.value || "")}
                  isDisabled={modalType === "edit"}
                  options={karyawanData}
                  placeholder="-- Pilih Karyawan --"
                  noOptionsMessage={() => "Tidak ada data karyawan"}
                  className="w-full"
                  classNamePrefix="react-select"
                />
              </div>

              {modalType === "add" && kontrakList.length > 0 && (
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2">
                    Periode Saldo *
                  </label>
                  <select
                    name="tkontrak_kerja_id"
                    value={formData.tkontrak_kerja_id}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded bg-white focus:outline-none focus:border-blue-500"
                  >
                    {kontrakList.map((k) => (
                      <option key={k.tkontrak_kerja_id} value={k.tkontrak_kerja_id}>
                        {k.periode}
                        {k.jumlah != null ? ` — sudah diisi ${k.jumlah} hari` : ""}
                      </option>
                    ))}
                    <option value="">Per Tahun (di luar periode kontrak)</option>
                  </select>
                  <p className="text-xs text-gray-500 mt-1">
                    Karyawan PKWT: hak cuti berlaku selama periode kontrak; sisa tidak dibawa ke kontrak berikutnya.
                  </p>
                </div>
              )}
              {modalType === "edit" && formData.tkontrak_kerja_id && (
                <p className="text-sm text-gray-600">
                  Periode: {selectedData?.periode}
                </p>
              )}

              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">
                  Jumlah Cuti *
                </label>
                <Input
                  type="number"
                  name="jumlah"
                  value={formData.jumlah}
                  onChange={handleInputChange}
                  color="blue"
                  min="1"
                />
              </div>

              {!formData.tkontrak_kerja_id && (
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2">
                    Tahun *
                  </label>
                  <Input
                    type="number"
                    name="tahun"
                    value={formData.tahun}
                    onChange={handleInputChange}
                    color="blue"
                    disabled={modalType === "edit"}
                  />
                </div>
              )}

              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">
                  Keterangan
                </label>
                <textarea
                  name="keterangan"
                  value={formData.keterangan}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:border-blue-500"
                  rows="3"
                  placeholder="Keterangan (opsional)"
                />
              </div>
            </div>
          )}
        </ModalBody>
        <ModalFooter>
          <Button
            color="gray"
            buttonType="outline"
            size="md"
            onClick={() => {
              setShowModal(false);
              resetForm();
            }}
          >
            Batal
          </Button>
          {modalType === "delete" ? (
            <Button
              color="red"
              buttonType="solid"
              size="md"
              onClick={handleDelete}
              disabled={loading}
            >
              {loading ? "Menghapus..." : "Hapus"}
            </Button>
          ) : (
            <Button
              color={modalType === "add" ? "green" : "lightBlue"}
              buttonType="solid"
              size="md"
              onClick={modalType === "add" ? handleSubmit : handleUpdate}
              disabled={loading}
            >
              {loading ? "Menyimpan..." : "Simpan"}
            </Button>
          )}
        </ModalFooter>
      </Modal>
    </>
  );
};

export default InputSaldoCutiPage;
